import { useEffect, useRef, useState } from "react"

const BASE_CHARS_PER_SEC = 90
const BURST_THRESHOLD = 250
const BURST_CHARS_PER_SEC = 320

/**
 * Reveals `target` char-by-char while `enabled` is true.
 *
 * Time-paced via requestAnimationFrame so the visual rate stays smooth
 * (~90 chars/sec) regardless of how chunks arrive from the server. If the
 * backlog grows beyond BURST_THRESHOLD chars (e.g. user reopens a tab mid
 * stream), it ramps up to BURST_CHARS_PER_SEC to avoid falling behind.
 *
 * When `enabled` flips to false, snaps to the full target so completed
 * messages render fully.
 */
export const useTypewriter = (target: string, enabled: boolean): string => {
  const [displayed, setDisplayed] = useState(enabled ? "" : target)
  const targetRef = useRef(target)
  const lastTimeRef = useRef(0)

  useEffect(() => {
    targetRef.current = target
  }, [target])

  useEffect(() => {
    if (!enabled) {
      lastTimeRef.current = 0
      setDisplayed(targetRef.current)
      return
    }

    let raf = 0
    const tick = (now: number) => {
      if (lastTimeRef.current === 0) lastTimeRef.current = now
      const dt = (now - lastTimeRef.current) / 1000
      let nextDisplayed: string | null = null

      setDisplayed((prev) => {
        const target = targetRef.current
        const gap = target.length - prev.length
        if (gap <= 0) return prev

        const rate =
          gap > BURST_THRESHOLD ? BURST_CHARS_PER_SEC : BASE_CHARS_PER_SEC
        const due = Math.floor(dt * rate)
        if (due < 1) return prev

        nextDisplayed = target.slice(0, prev.length + Math.min(due, gap))
        return nextDisplayed
      })

      if (nextDisplayed !== null) {
        lastTimeRef.current = now
      }

      raf = requestAnimationFrame(tick)
    }

    lastTimeRef.current = 0
    raf = requestAnimationFrame(tick)

    return () => {
      lastTimeRef.current = 0
      cancelAnimationFrame(raf)
    }
  }, [enabled])

  return enabled ? displayed : target
}
